import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpSodertaljePage: ServicePageConfig = {
  slug: "flytthjalp-sodertalje",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Södertälje – Skåpbil och bärare | Ungdomskraft",
    description:
      "Boka flytthjälp i Södertälje. Vi bär, kör och lossar i Östertälje, Ronna, Pershagen, Järna och Enhörna. Fast paketpris, RUT-avdrag och ofta snabb tid.",
    h1: "Flytthjälp Södertälje",
  },

  hero: {
    heading: "Flytthjälp i Södertälje",
    intro:
      "Ska du flytta inom Södertälje eller till eller från kommunen? Vi kommer med skåpbil och unga, starka bärare till lägenheten i Hovsjö, radhuset i Östertälje eller villan i Pershagen. Vi bär ut, lastar, kör längs E4/E20 eller genom stan och ställer allt på plats på nya adressen. Du ser paketpriset direkt när du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt:
      "Ungdomskraft lastar en skåpbil vid flytthjälp i Södertälje",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har hjälpt hushåll i Stockholms län att flytta sedan 2014. Våra bärare är tränade i lyftteknik och lastning och vet hur man tar en tvättmaskin nedför ett hisslöst trapphus i ett sextiotalshus lika väl som en soffa ut ur ett villagarage i Pershagen.",
    bookYourText: "Boka flytten",
    bookDescText:
      "Skriv in adressen du flyttar från och adressen du flyttar till, välj paket efter hur mycket som ska med och välj en tid. Priset syns innan du bekräftar.",
    vehicleTitleText: "Bil och bärare i samma bokning",
    vehicleDescText:
      "Du slipper hyra släp eller låna en kompis bil. Vi kommer med skåpbil, möbelfiltar och spännband och kör hela vägen, oavsett om det är några kvarter i centrum eller ut till Järna.",
  },

  itemsSection: {
    heading: "Det här flyttar vi oftast i Södertälje",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Hörnsoffor och fåtöljer, lindade i filt före bärningen",
        image: "/sofa.webp",
        alt: "Soffa som bärs ut vid flytthjälp i Södertälje",
      },
      {
        name: "Sängar & madrasser",
        description: "Ramsängar, kontinentalsängar och bäddmadrasser",
        image: "/bed.webp",
        alt: "Säng som flyttas från lägenhet i Ronna, Södertälje",
      },
      {
        name: "Bord & stolar",
        description: "Matbord, skrivbord och stolar – bordsben skruvas loss vid behov",
        image: "/table.webp",
        alt: "Matbord och stolar vid flytt i Östertälje",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, frys och kyl säkrade för transport",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas från villa i Pershagen, Södertälje",
      },
      {
        name: "Garderober & hyllor",
        description: "Garderober, byråer och bokhyllor, demonterade om de inte går hela",
        image: "/wardrobe.webp",
        alt: "Garderob som demonteras inför flytt i Södertälje",
      },
      {
        name: "Flyttkartonger",
        description: "Packade kartonger med kök, kläder, böcker och barnsaker",
        image: "/box.webp",
        alt: "Flyttkartonger som lastas i skåpbil i Järna",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Södertälje – från Hovsjö till Enhörna",
    mainText:
      "Södertälje är en stad med tydligt olika bostadsmiljöer, och flytten ser därefter ut. Hovsjö, Ronna, Geneta och Fornhöjden byggdes till stor del under miljonprogrammet kring 1970, med stora gårdar och flerbostadshus där det ofta är långt från porten till parkeringen. I Pershagen ligger sextiotalsvillor med garage och förråd, och i Enhörna och kring Järna och Mölnbo bor många på landet med gårdsplan och uthus. Centrum och Östertälje har äldre stenhus och nyare hyresrätter om vartannat. Därför frågar vi vid bokningen om våning, hiss och avstånd till entrén – det avgör hur många som bär och hur lång tid det tar.\n\nMånga flyttar till Södertälje från Stockholms innerstad eller söderort, ofta för att få mer yta eller ett hus med trädgård. En sådan flytt är knappt fyra mil längs E4/E20, och då lönar det sig att packa bilen rätt från början så att allt får plats på en tur. Vi lägger tunga saker som vitvaror och byråer längst in, soffor och sängar stående längs väggarna och kartongerna överst. Flyttar du i stället inom kommunen, till exempel från en lägenhet i Brunnsäng till ett radhus i Östertälje, blir körningen kort och det mesta av tiden går till att bära.\n\nPendeltåget stannar vid Södertälje centrum, Södertälje hamn och Östertälje, och grenen mot Gnesta via Södertälje syd når Järna och Mölnbo. Det betyder att många bostäder ligger nära stationen men långt från en bra lastplats. Vi planerar var bilen kan stå och i vilken ordning sakerna ska bäras, så att bärarna inte går i onödan. I centrum kan gatorna vara smala och lastplatserna få; har huset en gård eller en infart vi får använda, säg det gärna när du bokar.",
    preparationHeading: "Så förbereder du flytten i Södertälje",
    preparationText:
      "Packa lösa saker i kartonger och skriv på varje kartong vilket rum den ska till. Det är det som sparar mest tid, eftersom vi då kan bära rakt in i rätt rum på nya adressen. Plocka ur garderober och byråer – en tom byrå är lätt att bära, en full är det inte. Frosta av frysen dagen innan.\n\nBor du i ett av de stora bostadsområdena, som Hovsjö eller Ronna, ta reda på om bilen får köra fram till porten eller om det finns en lastplats på gården. I villaområden som Pershagen hjälper det om uppfarten är fri så att vi kan backa nära entrén. Flyttar du till landet i Enhörna eller runt Järna, kontrollera att grusvägen fram till huset går att köra med skåpbil och berätta om det finns grind eller bom. Ha portkoder och nycklar redo för båda adresserna.",
    additionalSections: [
      {
        heading: "Områden vi flyttar i, i Södertälje kommun",
        content:
          "Vi tar flyttuppdrag i hela Södertälje kommun. I staden: Centrum, Östertälje, Västergård, Brunnsäng, Hovsjö, Ronna, Geneta, Fornhöjden, Saltskog och Pershagen. Utanför staden: Järna, Mölnbo och Enhörna.\n\nVi flyttar också mellan Södertälje och resten av länet – till och från Stockholm, Huddinge, Botkyrka, Salem och Nykvarn. Körsträcka inom 20 km ingår i paketpriset. Längre sträckor, till exempel innerstaden till Södertälje, debiteras per kilometer enligt paketet, och du ser hela beloppet innan du bokar.",
      },
      {
        heading: "Från lägenhet till villa – en vanlig flytt till Södertälje",
        content:
          "En typisk flytt vi gör går från en tvåa eller trea i Stockholm till ett hus i Södertälje. Bohaget brukar vara större än man tror: förutom möbler och kartonger kommer cyklar, balkongmöbler och förrådets innehåll. Välj ett större paket än du tror att du behöver hellre än ett för litet, så slipper du en extra tur. Har du saker du inte vill ha med, kan vi ta dem till återvinning i samma veva om du bokar bortforsling.",
        cta: { text: "Se bortforsling i Stockholm →", href: "/bortforsling-stockholm" },
      },
      {
        heading: "Varför boka flytthjälp i Södertälje med Ungdomskraft?",
        listItems: [
          {
            label: "Vi känner bostäderna –",
            text: "Miljonprogramsgårdarna i Hovsjö och Ronna, villagatorna i Pershagen och landsvägarna i Enhörna kräver olika planering. Vi frågar rätt saker vid bokningen.",
          },
          {
            label: "Pris innan du bokar –",
            text: "Du väljer paket och ser beloppet direkt, inklusive körsträcka. Ingen offertförfrågan och inget timpris som rinner iväg.",
          },
          {
            label: "RUT-avdrag på fakturan –",
            text: "Flytthjälp är RUT-berättigad. Du betalar halva arbetskostnaden och vi sköter ansökan.",
          },
          {
            label: "Försäkrat –",
            text: "Uppdragen är försäkrade genom Trygg Hansa, både dina saker och fastigheten vi bär i.",
          },
          {
            label: "Helger och kvällar –",
            text: "Vi flyttar alla dagar i veckan, så du behöver inte ta ledigt för att flytta till eller från Södertälje.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Södertälje?",
      answer:
        "Du väljer paket efter mängd: Liten (995 kr) för 1–2 föremål, Mellan (1 495 kr) för 3–6, Stor (2 495 kr) för 7–10, Extra Stor (4 995 kr, upp till 4 h) och Mega Stor (8 995 kr, upp till 8 h) för ett helt hem. Körsträcka inom 20 km ingår; längre sträckor debiteras per kilometer. Du ser totalen innan du bokar.",
    },
    {
      question: "Kostar det mer att flytta från Stockholm till Södertälje?",
      answer:
        "Körsträcka inom 20 km ingår i paketet. En flytt från innerstaden till Södertälje är längre än så, och de extra kilometrarna debiteras enligt paketets kilometerpris. Beloppet räknas ut när du skriver in adresserna, så du ser det innan du bekräftar.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Södertälje?",
      answer:
        "Ja. Flytthjälp ger RUT-avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Vi drar av det direkt på fakturan.",
    },
    {
      question: "Bär ni i miljonprogramshus utan hiss i Hovsjö och Ronna?",
      answer:
        "Ja. Många hus i Hovsjö, Ronna och Geneta har tre våningar och ingen hiss, och där bär vi allt i trappan. Ange våning och om det finns hiss när du bokar, så skickar vi rätt antal bärare.",
    },
    {
      question: "Flyttar ni till landet i Enhörna, Järna och Mölnbo?",
      answer:
        "Ja, så länge adressen går att nå med skåpbil. Berätta om det är grusväg, grind eller lång uppfart, så räknar vi med det i planeringen.",
    },
    {
      question: "Hur snabbt kan ni flytta mig i Södertälje?",
      answer:
        "Ofta finns tider inom några dagar, ibland samma vecka. Vid bokningen ser du vilka dagar och tider som är lediga för just dina adresser.",
    },
    {
      question: "Ingår skåpbil i flytthjälpen?",
      answer:
        "Ja, alla paket från Liten och uppåt har två personer och skåpbil. Du behöver inte hyra bil.",
    },
    {
      question: "Kan ni flytta tvättmaskin och kyl?",
      answer:
        "Ja. Vi kopplar loss, säkrar trumman där det behövs och bär. Frosta av frysen och töm kylen dagen före.",
    },
    {
      question: "Monterar ni isär och ihop möbler?",
      answer:
        "Ja, vi tar isär sängar, garderober och bord som inte går igenom dörren och sätter ihop dem igen på nya adressen. Lägg skruvarna i en märkt påse.",
    },
    {
      question: "Kan ni köra flera turer om allt inte får plats?",
      answer:
        "Ja. Vid en stor villaflytt, till exempel i Pershagen med garage och förråd, kan det behövas två turer. Välj Extra Stor eller Mega Stor så har vi tid för det.",
    },
    {
      question: "Flyttar ni på helger i Södertälje?",
      answer:
        "Ja, vi flyttar alla dagar i veckan, även lördag och söndag.",
    },
    {
      question: "Skyddar ni golv och trapphus?",
      answer:
        "Ja, vi har möbelfiltar och bärremmar och är försiktiga med dörrkarmar och väggar. Uppdragen är försäkrade genom Trygg Hansa.",
    },
    {
      question: "Vilka områden i Södertälje täcker ni?",
      answer:
        "Hela kommunen: Centrum, Östertälje, Västergård, Brunnsäng, Hovsjö, Ronna, Geneta, Fornhöjden, Saltskog, Pershagen, Järna, Mölnbo och Enhörna.",
    },
    {
      question: "Vilka andra kommuner i Stockholms län flyttar ni i?",
      answer: `Vi flyttar i hela Stockholmsregionen: ${stockholmKommunerText}. Priset räknas likadant överallt – paketpris plus körsträcka över 20 km.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Södertälje i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbil, bär ut allt och lastar så att det tunga ligger stadigt. Sedan kör vi till nya adressen, i stan eller längs E4.",
    step3Title: "Inburet och klart",
    step3Desc:
      "Vi bär in möbler och kartonger i rätt rum och skruvar ihop det vi tog isär. Du kan börja packa upp samma dag.",
    ctaHeading: "Boka flytthjälp i Södertälje",
    ctaText:
      "Skriv in båda adresserna, välj paket och tid. Du ser priset direkt, med bil och bärare inräknade.",
  },

  en: {
    slug: "moving-help-sodertalje",
    meta: {
      title: "Moving Help Södertälje – Van & Crew | Ungdomskraft",
      description:
        "Book moving help in Södertälje. We carry, drive and unload in Östertälje, Ronna, Pershagen, Järna and Enhörna. Fixed package price and RUT deduction.",
      h1: "Moving Help Södertälje",
    },
    hero: {
      heading: "Moving Help in Södertälje",
      intro:
        "Moving within Södertälje, or to or from the municipality? We arrive with a van and young, strong carriers at the flat in Hovsjö, the terraced house in Östertälje or the villa in Pershagen. We carry out, load, drive along the E4/E20 or across town and place everything at the new address. You see the package price as soon as you book.",
      heroImageAlt: "Ungdomskraft loading a van during a move in Södertälje",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has helped households across Stockholm County move since 2014. Our carriers are trained in lifting and loading and know how to take a washing machine down the stairs of a 1960s block without a lift just as well as a sofa out of a villa garage in Pershagen.",
      bookYourText: "Book the move",
      bookDescText:
        "Enter the address you are leaving and the one you are moving to, choose a package by how much is coming along and pick a time. The price is shown before you confirm.",
      vehicleTitleText: "Van and carriers in one booking",
      vehicleDescText:
        "No need to rent a trailer or borrow a friend's car. We bring a van, furniture blankets and straps and drive the whole way, whether it is a few blocks in the centre or out to Järna.",
    },
    itemsSection: {
      heading: "What we move most often in Södertälje",
      items: [
        { name: "Sofas & armchairs", description: "Corner sofas and armchairs, wrapped in blankets before carrying", image: "/sofa.webp", alt: "Sofa being carried out during a move in Södertälje" },
        { name: "Beds & mattresses", description: "Bed frames, continental beds and mattress toppers", image: "/bed.webp", alt: "Bed being moved from a flat in Ronna, Södertälje" },
        { name: "Tables & chairs", description: "Dining tables, desks and chairs – legs removed when needed", image: "/table.webp", alt: "Dining table and chairs during a move in Östertälje" },
        { name: "Appliances", description: "Washing machine, freezer and fridge secured for transport", image: "/washer.webp", alt: "Washing machine moved from a villa in Pershagen, Södertälje" },
        { name: "Wardrobes & shelves", description: "Wardrobes, dressers and bookcases, taken apart if they won't fit whole", image: "/wardrobe.webp", alt: "Wardrobe being dismantled before a move in Södertälje" },
        { name: "Moving boxes", description: "Packed boxes with kitchenware, clothes, books and toys", image: "/box.webp", alt: "Moving boxes loaded into a van in Järna" },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Södertälje – from Hovsjö to Enhörna",
      mainText:
        "Södertälje has clearly different kinds of housing, and moves differ accordingly. Hovsjö, Ronna, Geneta and Fornhöjden were largely built during the Million Programme around 1970, with big courtyards and apartment blocks where the walk from the entrance to the parking is often long. Pershagen has 1960s villas with garages and storage, and in Enhörna and around Järna and Mölnbo many people live in the countryside with yards and outbuildings. The centre and Östertälje mix older stone buildings with newer rentals. That is why we ask about floor, lift and distance to the entrance when you book – it decides how many carriers we send and how long it takes.\n\nMany people move to Södertälje from central Stockholm or the southern suburbs, often for more space or a house with a garden. That move is just under 40 km along the E4/E20, so it pays to load the van right from the start so everything fits in one trip. We put heavy items like appliances and dressers furthest in, sofas and beds upright along the walls and boxes on top. If you are moving within the municipality instead, for example from a flat in Brunnsäng to a terraced house in Östertälje, the drive is short and most of the time goes to carrying.\n\nThe commuter train stops at Södertälje centrum, Södertälje hamn and Östertälje, and the branch towards Gnesta via Södertälje syd reaches Järna and Mölnbo. So many homes are close to a station but far from a good loading spot. We plan where the van can stand and in which order things are carried, so the carriers don't walk more than they need to. Streets in the centre can be narrow with few loading bays; if the building has a courtyard or driveway we may use, tell us when you book.",
      preparationHeading: "How to prepare your move in Södertälje",
      preparationText:
        "Pack loose items in boxes and write on each box which room it belongs in. That saves the most time, because we can carry straight into the right room at the new address. Empty wardrobes and dressers – an empty dresser is easy to carry, a full one is not. Defrost the freezer the day before.\n\nIf you live in one of the large estates, such as Hovsjö or Ronna, find out whether the van may drive up to the entrance or whether there is a loading spot in the courtyard. In villa areas like Pershagen it helps if the driveway is clear so we can reverse close to the door. If you are moving to the countryside in Enhörna or around Järna, check that the gravel road up to the house can take a van and tell us about any gate or barrier. Have door codes and keys ready for both addresses.",
      additionalSections: [
        {
          heading: "Areas we move in, in Södertälje Municipality",
          content:
            "We take moving jobs across the whole of Södertälje Municipality. In town: Centrum, Östertälje, Västergård, Brunnsäng, Hovsjö, Ronna, Geneta, Fornhöjden, Saltskog and Pershagen. Outside town: Järna, Mölnbo and Enhörna.\n\nWe also move between Södertälje and the rest of the county – to and from Stockholm, Huddinge, Botkyrka, Salem and Nykvarn. Driving within 20 km is included in the package price. Longer distances, such as central Stockholm to Södertälje, are charged per kilometre according to the package, and you see the full amount before you book.",
        },
        {
          heading: "From flat to house – a common move to Södertälje",
          content:
            "A typical move we do goes from a one- or two-bedroom flat in Stockholm to a house in Södertälje. Households tend to be bigger than people think: besides furniture and boxes there are bikes, balcony furniture and the contents of the storage room. Choose a larger package rather than one that is too small, so you avoid an extra trip. If there are things you don't want to bring, we can take them to recycling at the same time if you book junk removal.",
          cta: { text: "See junk removal in Stockholm →", href: "/en/junk-removal-stockholm" },
        },
        {
          heading: "Why book moving help in Södertälje with Ungdomskraft?",
          listItems: [
            { label: "We know the housing –", text: "The Million Programme courtyards in Hovsjö and Ronna, the villa streets of Pershagen and the country roads of Enhörna need different planning. We ask the right questions when you book." },
            { label: "Price before you book –", text: "You choose a package and see the amount straight away, driving included. No quote requests and no hourly rate that runs away." },
            { label: "RUT deduction on the invoice –", text: "Moving help qualifies for RUT. You pay half the labour cost and we handle the claim." },
            { label: "Insured –", text: "Jobs are insured through Trygg Hansa, covering both your belongings and the building we carry through." },
            { label: "Weekends and evenings –", text: "We move every day of the week, so you don't need time off to move to or from Södertälje." },
          ],
        },
      ],
    },
    faq: [
      { question: "How much does moving help in Södertälje cost?", answer: "You choose a package by volume: Small (SEK 995) for 1–2 items, Medium (SEK 1,495) for 3–6, Large (SEK 2,495) for 7–10, Extra Large (SEK 4,995, up to 4 h) and Mega Large (SEK 8,995, up to 8 h) for a whole home. Driving within 20 km is included; longer distances are charged per kilometre. You see the total before you book." },
      { question: "Does it cost more to move from Stockholm to Södertälje?", answer: "Driving within 20 km is included in the package. A move from central Stockholm to Södertälje is longer than that, and the extra kilometres are charged at the package's per-km rate. The amount is calculated when you enter the addresses, so you see it before you confirm." },
      { question: "Can I get a RUT deduction on moving help in Södertälje?", answer: "Yes. Moving help gives a RUT deduction of 50% of the labour cost, up to SEK 75,000 per person per year. We deduct it directly on the invoice." },
      { question: "Do you carry in Million Programme buildings without lifts in Hovsjö and Ronna?", answer: "Yes. Many buildings in Hovsjö, Ronna and Geneta have three floors and no lift, and there we carry everything by the stairs. State the floor and whether there is a lift when you book, and we send the right number of carriers." },
      { question: "Do you move to the countryside in Enhörna, Järna and Mölnbo?", answer: "Yes, as long as the address can be reached by van. Tell us about gravel roads, gates or a long driveway and we plan for it." },
      { question: "How soon can you move me in Södertälje?", answer: "There are often slots within a few days, sometimes the same week. When booking you see which days and times are free for your addresses." },
      { question: "Is a van included?", answer: "Yes, every package from Small upwards has two people and a van. You don't need to rent a vehicle." },
      { question: "Can you move a washing machine and fridge?", answer: "Yes. We disconnect, secure the drum where needed and carry. Defrost the freezer and empty the fridge the day before." },
      { question: "Do you take furniture apart and put it back together?", answer: "Yes, we dismantle beds, wardrobes and tables that won't fit through the door and reassemble them at the new address. Keep the screws in a labelled bag." },
      { question: "Can you make several trips if everything doesn't fit?", answer: "Yes. A big villa move, for example in Pershagen with a garage and storage, may need two trips. Choose Extra Large or Mega Large so we have time for it." },
      { question: "Do you move at weekends in Södertälje?", answer: "Yes, we move every day of the week, including Saturday and Sunday." },
      { question: "Do you protect floors and stairwells?", answer: "Yes, we bring furniture blankets and carrying straps and take care with door frames and walls. Jobs are insured through Trygg Hansa." },
      { question: "Which areas of Södertälje do you cover?", answer: "The whole municipality: Centrum, Östertälje, Västergård, Brunnsäng, Hovsjö, Ronna, Geneta, Fornhöjden, Saltskog, Pershagen, Järna, Mölnbo and Enhörna." },
      { question: "Which other municipalities in Stockholm County do you serve?", answer: `We move across the Stockholm region: ${stockholmKommunerText}. The price works the same everywhere – package price plus driving beyond 20 km.` },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Södertälje in 3 steps",
      step2Text:
        "The carriers arrive with a van, carry everything out and load it so the heavy items sit firmly. Then we drive to the new address, across town or along the E4.",
      step3Title: "Carried in and done",
      step3Desc:
        "We carry furniture and boxes into the right rooms and reassemble what we took apart. You can start unpacking the same day.",
      ctaHeading: "Book moving help in Södertälje",
      ctaText:
        "Enter both addresses, choose a package and a time. You see the price straight away, with van and carriers included.",
    },
  },
};
